// Implements: F-UIX-009, F-UIX-018, F-UIX-019, F-ITK-007
/**
 * The sidebar of the redesign (UX-03), as data: three groups — Trabajo, Organización, Plataforma —
 * built from the server's permission answer and nothing else (never a role name, never a client
 * switch: the mockup's "Vista como" is not product). The Ctrl+K palette reads the same model, so a
 * destination a person cannot open is neither drawn nor offered.
 *
 * Hiding is a convenience: every route keeps its own guard and the server decides every read.
 *
 * "Lienzo" (UX-04) heads the work group: it is where a mockup is created (`/lienzo`) and edited
 * (`/lienzo/:id`, and the version pages under `/mockups/:id/versions/…`), so it is the item marked
 * on all of them; the gallery only finds and organises. The platform items are the four sub-pages
 * of `/plataforma` (UX-11), each behind its own platform action; `/admin` and `/plataforma` only
 * redirect to the first one the reader may open (`firstPlatformHref`).
 */

import type { IconName } from "../../shared/design-system/icons";
import type { TranslationKey } from "../../shared/i18n";
import { admits } from "../../modules/backoffice/model/sections";
import type { BackofficeAudience } from "../../modules/backoffice/model/sections";
import { platformAllows } from "../../modules/admin/model/platformAccess";
import type { PlatformAction } from "../../modules/admin/model/platformAccess";
import type { BackofficeScope } from "../../types/api";

/** A sidebar label: an `app.nav.*` key, none of which takes a parameter. */
export type NavLabelKey = Extract<TranslationKey, `app.nav.${string}`>;

export interface NavItem {
  readonly id: string;
  readonly href: string;
  readonly labelKey: NavLabelKey;
  readonly icon: IconName;
}

export interface NavGroup {
  readonly id: "work" | "organization" | "platform";
  readonly labelKey: NavLabelKey;
  readonly items: readonly NavItem[];
}

export interface NavigationInput {
  /** The role-less scope: the sidebar can only follow `permissions`, never a role (UIX.5). */
  readonly scopes: readonly BackofficeScope[];
  /** Whether the server answers the person's WhatsApp numbers (intake enabled). */
  readonly intakeEnabled: boolean;
}

const WORK: readonly NavItem[] = [
  { id: "lienzo", href: "/lienzo", labelKey: "app.nav.lienzo", icon: "canvas" },
  { id: "gallery", href: "/", labelKey: "app.nav.gallery", icon: "grid" },
  { id: "design", href: "/design/profiles", labelKey: "app.nav.design", icon: "palette" },
  { id: "sharing", href: "/sharing", labelKey: "app.nav.sharing", icon: "link" },
  { id: "whatsapp", href: "/whatsapp", labelKey: "app.nav.whatsapp", icon: "message" },
];

/** The organization items with the audiences of their backoffice sections (`app/backoffice.tsx`). */
const ORGANIZATION: readonly (NavItem & { readonly audience: BackofficeAudience })[] = [
  // Mockup v3 P3: Personas, Equipos and Invitaciones head the group (v3 order).
  { id: "people", href: "/backoffice/people", labelKey: "app.nav.people", icon: "user", audience: ["member:update_role"] },
  {
    id: "teams",
    href: "/backoffice/teams",
    labelKey: "app.nav.teams",
    icon: "users",
    audience: ["team:create", "team_member:add"],
  },
  {
    id: "invitations",
    href: "/backoffice/invitations",
    labelKey: "app.nav.invitations",
    icon: "mail",
    audience: ["invitation:issue"],
  },
  {
    id: "consumption",
    href: "/backoffice/consumption",
    labelKey: "app.nav.consumption",
    icon: "chart",
    audience: ["usage:read_organization", "usage:read_team"],
  },
  {
    id: "distribution",
    href: "/backoffice/distribution",
    labelKey: "app.nav.distribution",
    icon: "pie",
    audience: ["usage_allocation:manage", "usage:read_team"],
  },
  { id: "audit", href: "/backoffice/audit", labelKey: "app.nav.audit", icon: "audit", audience: ["audit:read_organization"] },
];

/** The platform sub-pages (UX-11): the router mounts one page per path, each behind its action. */
export const PLATFORM_PATHS = {
  organizations: "/plataforma/organizaciones",
  totals: "/plataforma/totales",
  ceilings: "/plataforma/topes",
  models: "/plataforma/modelos",
  status: "/plataforma/estado",
} as const;

const PLATFORM: readonly (NavItem & { readonly action: PlatformAction })[] = [
  {
    id: "organizations",
    href: PLATFORM_PATHS.organizations,
    labelKey: "app.nav.platformOrganizations",
    icon: "building",
    action: "organization:set_status",
  },
  { id: "totals", href: PLATFORM_PATHS.totals, labelKey: "app.nav.platformTotals", icon: "sigma", action: "usage_totals:read" },
  { id: "ceilings", href: PLATFORM_PATHS.ceilings, labelKey: "app.nav.platformCeilings", icon: "gauge", action: "usage_ceiling:manage" },
  { id: "models", href: PLATFORM_PATHS.models, labelKey: "app.nav.platformModels", icon: "cpu", action: "model_settings:manage" },
  { id: "status", href: PLATFORM_PATHS.status, labelKey: "app.nav.platformStatus", icon: "activity", action: "system_status:read" },
];

const strip = ({ id, href, labelKey, icon }: NavItem): NavItem => ({ id, href, labelKey, icon });

/** The first platform sub-page `scopes` may open, in sidebar order, or `null` for none. */
export function firstPlatformHref(scopes: readonly BackofficeScope[]): string | null {
  return PLATFORM.find((item) => platformAllows(scopes, item.action))?.href ?? null;
}

/** The groups this person is shown, in sidebar order; an empty group is left out. */
export function buildNavigation({ scopes, intakeEnabled }: NavigationInput): NavGroup[] {
  const groups: NavGroup[] = [
    {
      id: "work",
      labelKey: "app.nav.groupWork",
      items: WORK.filter((item) => item.id !== "whatsapp" || intakeEnabled),
    },
    {
      id: "organization",
      labelKey: "app.nav.groupOrganization",
      items: ORGANIZATION.filter((item) => admits(item.audience, scopes)).map(strip),
    },
    {
      id: "platform",
      labelKey: "app.nav.platform",
      items: PLATFORM.filter((item) => platformAllows(scopes, item.action)).map(strip),
    },
  ];
  return groups.filter((group) => group.items.length > 0);
}

/** The item the current address belongs to, or `null` when it belongs to none. */
export function activeItemId(groups: readonly NavGroup[], pathname: string): string | null {
  const items = groups.flatMap((group) => group.items);
  const has = (id: string): boolean => items.some((item) => item.id === id);

  if (pathname === "/") return has("gallery") ? "gallery" : null;
  if (pathname === "/lienzo" || pathname.startsWith("/lienzo/") || pathname.startsWith("/mockups/")) {
    return has("lienzo") ? "lienzo" : null;
  }
  // A team's detail page belongs to Equipos.
  if (pathname.startsWith("/backoffice/teams/")) return has("teams") ? "teams" : null;
  return items.find((item) => item.href === pathname)?.id ?? null;
}
